import { UserPlus } from 'lucide-react';
import { useMutation } from '@platform/api-client';
import { Button, SearchSelect } from '@platform/design-system';
import type { TypedClient } from '@platform/openapi';
import { useState } from 'react';
import { prefillOf } from '../customers/customer-form.ts';
import { CustomerSheet } from '../customers/CustomerSheet.tsx';
import type { Customer, CustomerBody } from '../customers/customers.ts';
import { usePageContext } from '../usePageContext.ts';
import { useCustomerOptions } from './useCustomerOptions.ts';
import { useSearchSelectTexts } from './useSearchSelectTexts.ts';
import styles from './NewOrder.module.css';

export type CustomerPickerProps = {
  api: TypedClient;
  selected: Customer | null;
  errorText: string | undefined;
  onSelect: (customer: Customer | null) => void;
};

const placeOf = (customer: Customer): string | undefined =>
  [customer.city, customer.governorate].filter((part) => part !== null && part !== '').join(', ') ||
  undefined;

export const CustomerPicker = ({ api, selected, errorText, onSelect }: CustomerPickerProps) => {
  const { t, invalidate } = usePageContext();
  const [text, setText] = useState('');
  const [creating, setCreating] = useState(false);
  const [opened, setOpened] = useState(false);
  const found = useCustomerOptions(api, text, opened);
  const texts = useSearchSelectTexts(
    found.byName ? t('newOrder.customerByPhoneOnly') : t('newOrder.noCustomerFound'),
  );

  const create = useMutation({
    mutationFn: (body: CustomerBody) => api.post('/admin/customers', { body }),
    onSuccess: async (customer) => {
      setCreating(false);
      onSelect(customer);
      await invalidate('customers');
    },
  });

  const openHolder = useMutation({
    mutationFn: (customerId: string) => api.get('/admin/customers/{id}', { path: { id: customerId } }),
    onSuccess: (customer) => {
      setCreating(false);
      create.reset();
      onSelect(customer);
    },
  });

  if (selected !== null)
    return (
      <div className={styles['picked']}>
        <div>
          <strong>{selected.name}</strong>
          <div dir="ltr" className={styles['muted']}>
            {selected.phone}
          </div>
        </div>
        <Button
          variant="ghost"
          onClick={() => {
            setText('');
            onSelect(null);
          }}
        >
          {t('newOrder.changeCustomer')}
        </Button>
      </div>
    );

  const startCreating = () => {
    create.reset();
    setCreating(true);
  };

  return (
    <div className={styles['picker']}>
      <SearchSelect
        label={t('newOrder.customerSearch')}
        placeholder={t('newOrder.customerPlaceholder')}
        texts={texts}
        required
        errorText={errorText}
        query={text}
        options={found.customers.map((customer) => ({
          value: customer.id,
          label: customer.name,
          detail: customer.phone,
          aside: placeOf(customer),
        }))}
        loading={found.loading}
        hasMore={found.hasMore}
        loadingMore={found.loadingMore}
        footer={
          <Button
            icon={<UserPlus size={16} aria-hidden="true" />}
            variant="ghost"
            size="sm"
            onClick={startCreating}
          >
            {text.trim() === ''
              ? t('newOrder.createCustomer')
              : t('newOrder.createCustomerNamed', { text: text.trim() })}
          </Button>
        }
        onOpen={() => {
          setOpened(true);
        }}
        onQueryChange={setText}
        onLoadMore={found.loadMore}
        onPick={(customerId) => {
          const customer = found.customers.find((candidate) => candidate.id === customerId);
          if (customer !== undefined) onSelect(customer);
        }}
      />
      {creating ? (
        <CustomerSheet
          api={api}
          editing={null}
          prefill={prefillOf(text)}
          submitting={create.isPending}
          error={openHolder.error ?? create.error}
          onOpenHolder={(customerId) => {
            openHolder.mutate(customerId);
          }}
          onClose={() => {
            setCreating(false);
            create.reset();
          }}
          onSubmit={(body) => {
            create.mutate(body);
          }}
        />
      ) : null}
    </div>
  );
};
